// Presentation helpers for the game panel: dashboard numbers, countdown ring,
// result toast, modals (history / achievements / leaderboard / save score),
// achievement notifications.
import { el, find } from '../core/dom';
import { t } from '../core/i18n';
import { getJson, postJson } from '../core/api';
import { sound } from '../core/sound';
import type { LeaderboardDto, RoundResultDto, SessionStateDto, SubmitScoreResult, TradeLogEntry } from './types';

const ACHIEVEMENTS = ['firstWin', 'winStreak5', 'winStreak10', 'reach2000', 'reach5000', 'trades50', 'perfectDay'];
const ACHIEVEMENT_ICONS: Record<string, string> = {
  firstWin: '\u{1F3C6}',
  winStreak5: '\u{1F525}',
  winStreak10: '\u26A1',
  reach2000: '\u{1F4B0}',
  reach5000: '\u{1F48E}',
  trades50: '\u{1F4CA}',
  perfectDay: '\u2728',
};

const RING_LENGTH = 326.7; // 2 * PI * r(52)

export class GameUi {
  readonly trades: TradeLogEntry[] = [];
  private unlocked = new Set<string>();
  private lastBalance = 1000;
  private countdownTimer = 0;

  // ----- dashboard ------------------------------------------------------------

  applySession(session: SessionStateDto): void {
    this.unlocked = new Set(session.unlockedAchievements);
    this.setStats(session.balance, session.wins, session.losses, session.streak);
    const difficulty = find<HTMLSelectElement>('difficultySelect');
    if (difficulty) difficulty.value = session.difficulty;
    const mode = find<HTMLSelectElement>('gameModeSelect');
    if (mode) mode.value = session.mode;
  }

  setStats(balance: number, wins: number, losses: number, streak: number): void {
    el('balance').textContent = balance.toFixed(2);
    el('wins').textContent = String(wins);
    el('losses').textContent = String(losses);
    el('streak').textContent = String(streak);
    if (balance !== this.lastBalance) {
      const node = el('balance').parentElement!;
      node.classList.remove('bump');
      void node.offsetWidth; // restart the animation
      node.classList.add('bump');
      this.lastBalance = balance;
    }
  }

  setPrice(price: number, previous: number): void {
    el('currentPrice').textContent = price.toFixed(2);
    const tag = el('priceTag');
    tag.classList.toggle('game__price--up', price > previous);
    tag.classList.toggle('game__price--down', price < previous);
  }

  setStatus(state: 'connecting' | 'live' | 'paused' | 'error', key: string): void {
    const status = el('gameStatus');
    status.dataset.state = state;
    const text = el('gameStatusText');
    text.dataset.i18n = key;
    text.textContent = t(key);
  }

  setControlsEnabled(enabled: boolean): void {
    el<HTMLButtonElement>('buyBtn').disabled = !enabled;
    el<HTMLButtonElement>('sellBtn').disabled = !enabled;
  }

  // ----- countdown -------------------------------------------------------------

  startCountdown(deadlineMs: number, serverNowMs: number): void {
    const overlay = el('countdown');
    const bar = el<SVGCircleElement & HTMLElement>('countdownBar');
    const value = el('countdownValue');
    const skew = serverNowMs - Date.now();
    const total = deadlineMs - serverNowMs;
    overlay.hidden = false;
    let lastShown = Math.ceil(total / 1000);
    value.textContent = String(lastShown);

    cancelAnimationFrame(this.countdownTimer);
    const frame = (): void => {
      const remaining = deadlineMs - (Date.now() + skew);
      if (remaining <= 0) {
        this.stopCountdown();
        return;
      }
      const shown = Math.ceil(remaining / 1000);
      if (shown !== lastShown) {
        lastShown = shown;
        value.textContent = String(shown);
        sound.tick();
      }
      bar.style.strokeDashoffset = String(RING_LENGTH * (1 - remaining / total));
      this.countdownTimer = requestAnimationFrame(frame);
    };
    this.countdownTimer = requestAnimationFrame(frame);
  }

  stopCountdown(): void {
    cancelAnimationFrame(this.countdownTimer);
    el('countdown').hidden = true;
    el<HTMLElement>('countdownBar').style.strokeDashoffset = '0';
  }

  // ----- results ------------------------------------------------------------------

  showResult(result: RoundResultDto): void {
    const toast = el('resultToast');
    const prefix = result.isWin ? t('greatTrade') : t('badLuck');
    toast.textContent = `${prefix} $${result.amount.toFixed(2)}`;
    toast.className = `game__result game__result--${result.isWin ? 'win' : 'loss'}`;
    toast.hidden = false;
    window.setTimeout(() => {
      toast.hidden = true;
    }, 2200);

    this.trades.push({
      time: Date.now(),
      action: result.direction,
      entryPrice: result.entryPrice,
      exitPrice: result.exitPrice,
      isWin: result.isWin,
      amount: result.amount,
    });

    for (const id of result.newAchievements) {
      this.unlocked.add(id);
      this.notifyAchievement(id);
    }
  }

  private notifyAchievement(id: string): void {
    sound.achievement();
    const node = document.createElement('div');
    node.className = 'toast';
    node.innerHTML = `
      <div class="toast__icon">${ACHIEVEMENT_ICONS[id] ?? '\u{1F3C5}'}</div>
      <div><p class="toast__title">${t(id)}</p><p class="toast__text">${t(`${id}Desc`)}</p></div>`;
    document.body.appendChild(node);
    requestAnimationFrame(() => node.classList.add('toast--show'));
    setTimeout(() => {
      node.classList.remove('toast--show');
      setTimeout(() => node.remove(), 500);
    }, 3200);
  }

  // ----- modals ----------------------------------------------------------------------

  initModals(): void {
    for (const modal of document.querySelectorAll<HTMLElement>('.modal')) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal || (e.target as HTMLElement).closest('[data-close]')) modal.hidden = true;
      });
    }
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      for (const modal of document.querySelectorAll<HTMLElement>('.modal')) modal.hidden = true;
    });
  }

  openHistory(): void {
    const body = el('tradeLogBody');
    body.innerHTML = '';
    if (this.trades.length === 0) {
      body.innerHTML = `<tr><td colspan="6" class="trade-log__empty">${t('noTrades')}</td></tr>`;
    }
    this.trades.forEach((trade, index) => {
      const row = document.createElement('tr');
      const timestamp = new Date(trade.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      row.innerHTML = `
        <td>${index + 1}</td>
        <td>${timestamp}</td>
        <td><span class="tag tag--${trade.action}">${t(trade.action)}</span></td>
        <td>$${trade.entryPrice.toFixed(2)}</td>
        <td>$${trade.exitPrice.toFixed(2)}</td>
        <td class="amount--${trade.isWin ? 'win' : 'loss'}">${trade.isWin ? '+' : '-'}$${trade.amount.toFixed(2)}</td>`;
      body.appendChild(row);
    });
    el('historyModal').hidden = false;
  }

  openAchievements(): void {
    const grid = el('achievementsGrid');
    grid.innerHTML = '';
    for (const id of ACHIEVEMENTS) {
      const isUnlocked = this.unlocked.has(id);
      const card = document.createElement('div');
      card.className = `achievement achievement--${isUnlocked ? 'unlocked' : 'locked'}`;
      card.innerHTML = `
        <div class="achievement__icon">${ACHIEVEMENT_ICONS[id]}</div>
        <p class="achievement__name">${t(id)}</p>
        <p class="achievement__desc">${t(`${id}Desc`)}</p>`;
      grid.appendChild(card);
    }
    el('achievementsModal').hidden = false;
  }

  async openLeaderboard(): Promise<void> {
    const board = await getJson<LeaderboardDto>('/api/leaderboard');
    this.renderBoard(board);
    el('leaderboardModal').hidden = false;
  }

  private renderBoard(board: LeaderboardDto): void {
    const body = el('leaderboardBody');
    body.innerHTML = '';
    if (board.entries.length === 0) {
      body.innerHTML = `<tr><td colspan="3" class="trade-log__empty">${t('boardEmpty')}</td></tr>`;
    }
    board.entries.forEach((entry, index) => {
      const row = document.createElement('tr');
      row.innerHTML = `<td>${index + 1}</td><td></td><td>$${entry.score.toFixed(2)}</td>`;
      (row.children[1] as HTMLElement).textContent = entry.name;
      body.appendChild(row);
    });
    el('playerBest').textContent = `$${board.playerBest.toFixed(2)}`;
  }

  openSaveScore(balance: number): void {
    el('saveScoreValue').textContent = `$${balance.toFixed(2)}`;
    const input = el<HTMLInputElement>('playerNameInput');
    input.value = localStorage.getItem('playerName') ?? '';
    el('saveScoreError').hidden = true;
    el('saveScoreModal').hidden = false;
    input.focus();
  }

  async submitScore(): Promise<void> {
    const input = el<HTMLInputElement>('playerNameInput');
    const name = input.value.trim();
    const error = el('saveScoreError');
    const { ok, data } = await postJson<SubmitScoreResult>('/api/leaderboard', { name });
    if (!ok || !data?.accepted) {
      error.textContent = data?.error ?? 'Could not save the score. Check the name and try again.';
      error.hidden = false;
      return;
    }
    localStorage.setItem('playerName', name);
    el('saveScoreModal').hidden = true;
    this.renderBoard(data.board);
    el('leaderboardModal').hidden = false;
  }

  openGameOver(result: RoundResultDto): void {
    const won = result.challengeCompleted;
    el('gameOverEmoji').textContent = won ? '\u{1F3C6}' : '\u{1F4C9}';
    el('gameOverTitle').textContent = won ? t('challengeDone') : t('bustedTitle');
    el('gameOverText').textContent = won
      ? `$${result.balance.toFixed(2)}`
      : t('bustedText');
    el('gameOverModal').hidden = false;
  }
}
